import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { LojaPageContainer } from "../../common";
import { detalharPedidoLoja } from "services/lojaPedidos";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import { useMaterialUIController, setMessage } from "context";
import { extractPedidoDetalheResponse } from "utils/pedidoApiResponse";
import PedidoFaturamentoForm from "./components/PedidoFaturamentoForm";

/**
 * Fase 2 — Faturamento do pedido (página filha).
 * Rota: /loja/pedidos/:pedidoId/faturamento
 */
export default function LojaPedidoFaturamento() {
  const { pedidoId } = useParams<{ pedidoId: string }>();
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();

  const [detalhe, setDetalhe] = useState<IPedidoDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reloadDetalhe = useCallback(async () => {
    if (!pedidoId) return;
    const { data, status } = await detalharPedidoLoja(pedidoId);
    if (status === 200) {
      const p = extractPedidoDetalheResponse(data);
      if (p?.id) setDetalhe(p);
    }
  }, [pedidoId]);

  useEffect(() => {
    (async () => {
      setLoadError(null);
      if (!pedidoId) {
        setLoadError("Pedido não informado.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, status } = await detalharPedidoLoja(pedidoId);
        if (status === 200) {
          const p = extractPedidoDetalheResponse(data);
          if (p?.id) {
            setDetalhe(p);
          } else {
            setDetalhe(null);
            setLoadError("Resposta inválida ao carregar o pedido.");
          }
        } else {
          setDetalhe(null);
          setLoadError("Não foi possível carregar o pedido.");
        }
      } catch (e: unknown) {
        const text =
          e instanceof Error ? e.message : "Falha ao carregar o pedido.";
        setLoadError(text);
        setMessage(dispatch, { open: true, severity: "error", text });
      } finally {
        setLoading(false);
      }
    })();
  }, [pedidoId, dispatch]);

  const handleAfterEmit = useCallback(
    async (patch?: Partial<IPedidoDetalhe>) => {
      await reloadDetalhe();
      if (patch) {
        setDetalhe((d) => (d ? { ...d, ...patch } : d));
      }
    },
    [reloadDetalhe]
  );

  const voltar = () => {
    if (pedidoId) navigate(`/loja/pedidos/${pedidoId}`);
    else navigate(-1);
  };

  return (
    <LojaPageContainer
      title="Faturamento"
      subtitle={
        detalhe?.numero
          ? `Pedido ${detalhe.numero}`
          : pedidoId
            ? `Pedido ${pedidoId.slice(0, 8)}…`
            : undefined
      }
    >
      <MDBox mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <MDButton variant="outlined" color="dark" onClick={voltar}>
          Voltar ao pedido
        </MDButton>
      </MDBox>

      {loading ? (
        <MDTypography variant="button">Carregando…</MDTypography>
      ) : loadError ? (
        <MDTypography color="error" variant="button">
          {loadError}
        </MDTypography>
      ) : pedidoId && detalhe ? (
        <PedidoFaturamentoForm
          pedidoId={pedidoId}
          detalhe={detalhe}
          onAfterEmitSuccess={handleAfterEmit}
        />
      ) : (
        <MDTypography variant="button">Pedido não encontrado.</MDTypography>
      )}
    </LojaPageContainer>
  );
}
